import { ChangeEvent, useId, useRef, useState } from "react";
import clsx from "clsx"
import uniqBy from "lodash/uniqBy";

import { icons } from '@/shared/icons';
import { useClickOutside } from "@/hooks/useClickOutside";
import { Icon, type IconType } from "@/stories/atoms/Icon/Icon";
import { Tag, TagProps } from "@/stories/atoms/Tag/Tag";
import inputStyles from '@/stories/components/Form/Input/input.module.css'
import { DropdownPanel } from "../Dropdown/Dropdown";
import { FormItem } from "../FormItem/FormItem";
import { InputProps } from "../Input/Input";

import styles from './multi-value-list.module.css'

type ValueItem = { label: string, value: string }

/**
 * `className` et `style` s'appliquent au conteneur (FormItem),
 * les autres attributs HTML sont transmis à l'<input> de saisie.
 */
export type MultipleValueListProps = {
  values?: ValueItem[],
  setValues: (values: ValueItem[]) => void,
  list: ValueItem[],
  tagVariant?: TagProps['variant'],
  allowCustomValue?: boolean,
} & Omit<InputProps, 'id' | 'list' | 'value' | 'setValue'>

export const MultipleValueList = ({
  values = [],
  setValues,
  list,
  name,
  label,
  labelIcon,
  required = false,
  inline,
  helpText,
  errorText,
  hideLabel,
  hideOptionnal,
  icon,
  suffix,
  variant,
  tagVariant,
  allowCustomValue = true,
  className,
  style,
  ...inputProps
}: MultipleValueListProps) => {
  const id = useId()
  const panelRef = useRef(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState('')
  useClickOutside(panelRef, () => setOpen(false), open)

  const query = inputValue.trim()
  const options = [
    ...(allowCustomValue && query ? [{ label: query, value: query }] : []),
    ...list,
  ]
  const filteredList = uniqBy(
    options.filter(item =>
      !values.some(value => value.value === item.value)
      && item.label.toLocaleLowerCase().includes(query.toLocaleLowerCase())),
    'value'
  )

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setOpen(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Check if a modifier key (Alt, Shift, Ctrl, Meta) is pressed
    if (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return

    if (e.key === 'Enter') {
      e.preventDefault()
      if (allowCustomValue && query) handleAddValue({ label: query, value: query })
    }
  }

  const handleAddValue = (item: ValueItem) => {
    // ignore if a value with the same label is already selected
    if (values.some(value => value.label.toLocaleLowerCase() === item.label.toLocaleLowerCase())) return

    setValues([...values, item])
    setInputValue('')
    inputRef.current?.focus()
  }

  const handleRemoveValue = (remove: string) => {
    setValues(values.filter(value => value.value !== remove))
    inputRef.current?.focus()
  }

  return (
    <FormItem
      className={clsx(icon && 'has-icon', suffix && 'has-suffix', className)}
      style={style}
      id={id}
      label={label}
      labelIcon={labelIcon}
      inline={inline}
      hideLabel={hideLabel}
      hideOptionnal={hideOptionnal}
      required={required}
      helpText={helpText}
      errorText={errorText}>
      <div className={clsx(styles.inputWrapper, variant && inputStyles[variant])}>
        {icon ? <Icon icon={icon} className={inputStyles.icon}/> : null}
        <div className={styles.valuesList}>
          {values.map(value => (
            <Tag asChild key={value.value} variant={tagVariant} className={styles.valueTag}>
              <button type="button" onClick={() => handleRemoveValue(value.value)} aria-label={`Retirer ${value.label}`}>
                {value.label} <Icon icon="close" size={'1x'}/>
              </button>
            </Tag>
          ))}
          <input
            ref={inputRef}
            value={inputValue}
            onClick={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            className={clsx(inputStyles.input, variant && inputStyles[variant], styles.input)}
            id={id}
            type="text"
            // the text input is empty once values are selected: only require it while there are none
            required={required && values.length === 0}
            name={name}
            {...inputProps}/>
        </div>
        {suffix ? Object.keys(icons).includes(suffix) ? <Icon icon={suffix as IconType} className={inputStyles.suffix}/> : <span className={inputStyles.suffix}>{suffix}</span> : null}
      </div>
      <DropdownPanel open={open && filteredList.length > 0} ref={panelRef} id={id}>
        <div className={styles.listWrapper}>
          <div className={styles.list}>
            {filteredList.map((item) => (
              <button
                key={item.value}
                type="button"
                className={styles.item}
                onClick={() => handleAddValue(item)}>
                  {item.label}
              </button>
            ))}
          </div>
        </div>
      </DropdownPanel>
    </FormItem>
  )
}
